// src/pages/msp-inventory/pages/reports/StorageReportPage.jsx
import React, { useEffect, useState } from "react";
import { Package, AlertTriangle } from "lucide-react";
import KpiCard from "../../components/reports/KpiCard";
import ClickableSkuCell from "../../components/reports/ClickableSkuCell";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getStorageReport } from "../../services/catalogService";
import { useImageBackfill } from "../../hooks/useImageBackfill";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import { usePersistedState } from "../../hooks/usePersistedState";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { exportSkuReportCsv } from "../../utils/exportCsv";
import { buildCacheKey, getCachedReport, setCachedReport, invalidateCachedReport } from "../../utils/reportCache";
import RefreshReportBar from "../../components/reports/RefreshReportBar";
import LoadingGame from "../../components/reports/LoadingGame";

const AGED_THRESHOLD_DAYS = 365;

const StorageReportPage = ({ onOpenSku }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    const [filters, setFilters] = usePersistedState("reports:storage:filters", {
        statusFilter: "",
        sfStockMin: "", sfStockMax: "",
        wfsStockMin: "", wfsStockMax: "",
        daysMin: "", daysMax: "", onlyAged: false,
        hideZeroStock: true,
    });
    const { statusFilter, sfStockMin, sfStockMax, wfsStockMin, wfsStockMax, daysMin, daysMax, onlyAged, hideZeroStock } = filters;
    const patchFilters = (patch) => setFilters((f) => ({ ...f, ...patch }));

    const cacheKey = buildCacheKey("storage", {});

    const loadReport = async (forceRefresh = false) => {
        const isInitialLoad = !data;

        if (!forceRefresh) {
            const cached = await getCachedReport(cacheKey);
            if (cached) {
                setData(cached.data);
                setSavedAt(cached.savedAt);
                setLoading(false);
                return;
            }
        }

        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        try {
            const d = await getStorageReport();
            setData(d);
            setSavedAt(Date.now());
            setCachedReport(cacheKey, d);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleForceRefresh = async () => {
        await invalidateCachedReport(cacheKey);
        await loadReport(true);
    };

    const patchRow = (id, patch) => {
        setData((prev) => prev ? {
            ...prev,
            rows: prev.rows.map((r) => r.catalog_item_id === id ? { ...r, ...patch } : r),
        } : prev);
    };

    const availableStatuses = [...new Set((data?.rows || []).map((r) => r.published_status).filter(Boolean))];

    const visibleRows = (data?.rows || []).filter((r) => {
        if (hideZeroStock && r.total_units === 0) return false;
        if (statusFilter && r.published_status !== statusFilter) return false;
        if (sfStockMin !== "" && r.sf_units < Number(sfStockMin)) return false;
        if (sfStockMax !== "" && r.sf_units > Number(sfStockMax)) return false;
        if (wfsStockMin !== "" && r.wfs_units < Number(wfsStockMin)) return false;
        if (wfsStockMax !== "" && r.wfs_units > Number(wfsStockMax)) return false;
        if (daysMin !== "" && (r.days_in_storage ?? -1) < Number(daysMin)) return false;
        if (daysMax !== "" && (r.days_in_storage ?? Infinity) > Number(daysMax)) return false;
        if (onlyAged && !(r.units_aged_365_plus > 0)) return false;
        return true;
    });

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(visibleRows, { key: "days_in_storage", dir: "desc" }, "reports:storage:table");

    useImageBackfill(sorted, patchRow);

    if (loading && !data) return <LoadingGame label="Loading storage report..." />;
    if (!data) return null;

    const { totals } = data;
    const statusClasses = (status) =>
        status === "PUBLISHED" ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600";

    const daysClasses = (days) => {
        if (days == null) return "text-gray-300";
        if (days > AGED_THRESHOLD_DAYS) return "text-red-600 font-bold";
        if (days > 270) return "text-orange-600 font-semibold";
        if (days > 180) return "text-yellow-700 font-semibold";
        return "text-gray-600";
    };

    return (
        <div className="max-w-5xl mx-auto py-8 px-6">
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Package size={22} className="text-slate-600" /> Storage Report
                    </h1>
                    <p className="text-gray-500 text-sm">Unit counts and how long WFS stock has been sitting, right now</p>
                    <p className="text-gray-400 text-xs mt-1">
                        "Days in Storage" reflects Walmart's own aging bracket for your current on-hand units (not the SKU's original stocking date) — hover a row for the exact first-in-stock date, when available.
                    </p>
                </div>
                <RefreshReportBar savedAt={savedAt} refreshing={refreshing} onRefresh={handleForceRefresh} onAutoRefresh={handleForceRefresh} />
            </div>

            <div className="flex justify-end mb-4">
                <ExportCsvButton onExport={() => exportSkuReportCsv({
                    filename: `storage_report_${new Date().toISOString().slice(0, 10)}.csv`,
                    rows: sorted,
                    idKey: "catalog_item_id",
                    headers: ["Item", "SKU", "Status", "SF Units", "WFS Units", "Inbound Units", "Total Units", "Storage Age Bracket", "Units Aged 365+"],
                    getRowValues: (r) => [r.title, r.sku, r.published_status, r.sf_units, r.wfs_units, r.inbound_units, r.total_units, r.days_in_storage_label, r.units_aged_365_plus],
                    summaryLines: [
                        ["SF Units", totals.sf_units], ["WFS Units", totals.wfs_units],
                        ["Inbound Units", totals.inbound_units], ["Total Units", totals.total_units],
                        ["Units Aged 365+ Days", totals.units_aged_365_plus],
                    ],
                })} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <KpiCard label="SF Units" value={totals.sf_units} color="blue" />
                <KpiCard label="WFS Units" value={totals.wfs_units} color="teal" />
                <KpiCard label="Inbound Units" value={totals.inbound_units} color="orange" />
                <KpiCard label="Total Units" value={totals.total_units} color="purple" />
                <KpiCard label="Aged 365+ Days" value={totals.units_aged_365_plus} color="red" />
            </div>

            {totals.units_aged_365_plus > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-xs text-red-700 flex items-start gap-2">
                    <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>
                        <strong>{totals.units_aged_365_plus} unit{totals.units_aged_365_plus === 1 ? "" : "s"}</strong> across
                        your WFS inventory have been in storage for over {AGED_THRESHOLD_DAYS} days. Walmart charges
                        escalating long-term storage fees on aged inventory — review the items below and consider
                        discounting, bundling, or removing stock to avoid extra charges.
                    </span>
                </div>
            )}

            <div className="bg-white rounded-xl shadow p-4 mb-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end mb-3">
                    <div>
                        <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Status</label>
                        <select value={statusFilter} onChange={(e) => patchFilters({ statusFilter: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500">
                            <option value="">All statuses</option>
                            {availableStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Min Days in Storage</label>
                        <input type="number" min="0" value={daysMin} onChange={(e) => patchFilters({ daysMin: e.target.value })}
                            placeholder="0" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Max Days in Storage</label>
                        <input type="number" min="0" value={daysMax} onChange={(e) => patchFilters({ daysMax: e.target.value })}
                            placeholder="Any" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                    </div>
                    <div>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 cursor-pointer transition ${onlyAged ? "border-red-400 bg-red-50" : "border-gray-200"}`}>
                            <input type="checkbox" checked={onlyAged} onChange={(e) => patchFilters({ onlyAged: e.target.checked })}
                                className="accent-red-600" />
                            <span className={`text-xs font-bold ${onlyAged ? "text-red-700" : "text-gray-600"}`}>365+ days only</span>
                        </label>
                    </div>
                    <div>
                        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 cursor-pointer transition ${hideZeroStock ? "border-teal-400 bg-teal-50" : "border-gray-200"}`}>
                            <input type="checkbox" checked={hideZeroStock} onChange={(e) => patchFilters({ hideZeroStock: e.target.checked })}
                                className="accent-teal-600" />
                            <span className={`text-xs font-bold ${hideZeroStock ? "text-teal-700" : "text-gray-600"}`}>Hide zero-stock items</span>
                        </label>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <label className="text-[10px] font-bold text-blue-500 uppercase block mb-1">Min SF Stock</label>
                        <input type="number" min="0" value={sfStockMin} onChange={(e) => patchFilters({ sfStockMin: e.target.value })}
                            placeholder="0" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-blue-500 uppercase block mb-1">Max SF Stock</label>
                        <input type="number" min="0" value={sfStockMax} onChange={(e) => patchFilters({ sfStockMax: e.target.value })}
                            placeholder="Any" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-teal-600 uppercase block mb-1">Min WFS Stock</label>
                        <input type="number" min="0" value={wfsStockMin} onChange={(e) => patchFilters({ wfsStockMin: e.target.value })}
                            placeholder="0" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-teal-600 uppercase block mb-1">Max WFS Stock</label>
                        <input type="number" min="0" value={wfsStockMax} onChange={(e) => patchFilters({ wfsStockMax: e.target.value })}
                            placeholder="Any" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-5 overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-white">
                        <tr className="text-left text-gray-400 border-b border-gray-100">
                            <SortableTH label="Item" sortKey="title" sortConfig={sortConfig} onSort={requestSort} />
                            <SortableTH label="Status" sortKey="published_status" sortConfig={sortConfig} onSort={requestSort} />
                            <SortableTH label="SF Units" sortKey="sf_units" sortConfig={sortConfig} onSort={requestSort} />
                            <SortableTH label="WFS Units" sortKey="wfs_units" sortConfig={sortConfig} onSort={requestSort} />
                            <SortableTH label="Inbound Units" sortKey="inbound_units" sortConfig={sortConfig} onSort={requestSort} />
                            <SortableTH label="Total" sortKey="total_units" sortConfig={sortConfig} onSort={requestSort} />
                            <SortableTH label="Days in Storage" sortKey="days_in_storage" sortConfig={sortConfig} onSort={requestSort} />
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.map((r) => {
                            const aged = r.units_aged_365_plus > 0;
                            return (
                                <tr key={r.catalog_item_id} className={`border-b border-gray-50 ${aged ? "bg-red-50/40" : ""}`}>
                                    <td className="py-2"><ClickableSkuCell item={r} onOpen={onOpenSku} /></td>
                                    <td className="py-2">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${statusClasses(r.published_status)}`}>
                                            {r.published_status || "—"}
                                        </span>
                                    </td>
                                    <td className="py-2">{r.sf_units}</td>
                                    <td className="py-2">{r.wfs_units}</td>
                                    <td className="py-2">{r.inbound_units}</td>
                                    <td className="py-2 font-bold">{r.total_units}</td>
                                    <td className="py-2">
                                        <div className={`flex items-center gap-1.5 ${daysClasses(r.days_in_storage)}`}
                                            title={r.first_in_stock_date ? `First in WFS stock: ${new Date(r.first_in_stock_date).toLocaleDateString()}` : undefined}>
                                            {aged && <AlertTriangle size={12} className="text-red-500 flex-shrink-0" />}
                                            <span>{r.days_in_storage_label || "—"}</span>
                                        </div>
                                        {aged && (
                                            <p className="text-[10px] text-red-500 mt-0.5">
                                                {r.units_aged_365_plus} unit{r.units_aged_365_plus === 1 ? "" : "s"} over {AGED_THRESHOLD_DAYS}d — long-term storage fees may apply
                                            </p>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {pageItems.length === 0 && (
                            <tr><td colSpan={7} className="py-8 text-center text-gray-400">No items match your current filters.</td></tr>
                        )}
                    </tbody>
                </table>
                <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
            </div>
        </div>
    );
};

export default StorageReportPage;